import React, { useState, useRef, useEffect } from "react";
import { observer } from "mobx-react";
import aiAgentStore from "Stores/AIAgentStore";
import MessageBridge, { MessageTypes } from "Utils/MessageBridge";
import GeminiService from "../../Services/GeminiService";

import "./AIAgent.scss";

const AIAgentPanel = observer(({ iframeRef }) => {
  const { isOpen, toggle } = aiAgentStore;
  const [inputValue, setInputValue] = useState("");
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);

  // Theme State
  const [theme, setTheme] = useState(localStorage.getItem("ai_panel_theme") || "light");

  const toggleTheme = () => {
    const newTheme = theme === "light" ? "dark" : "light";
    setTheme(newTheme);
    localStorage.setItem("ai_panel_theme", newTheme);
  };

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [aiAgentStore.messages.length, loading, isOpen]);

  const handleRedirect = (path) => {
    console.log("Redirecting to:", path);
    MessageBridge.sendToApp(iframeRef, MessageTypes.REDIRECT, { path });
  };

  // Helper to parse bold text within a string
  const parseBold = (text) => {
    const parts = text.split(/(\*\*.*?\*\*)/g);
    return parts.map((part, index) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={index}>{part.slice(2, -2)}</strong>;
      }
      return part;
    });
  };

  const parseMessage = (text) => {
    if (!text) return "";
    // Regex to match [Link Text](path)
    const regex = /(\[.*?\]\(.*?\))/g;
    const parts = text.split(regex);

    return parts.map((part, index) => {
      // Check for Link
      const linkMatch = part.match(/\[(.*?)\]\((.*?)\)/);
      if (linkMatch) {
        const url = linkMatch[2];
        const isSupportLink = url.startsWith("https://support.salesgear.io");

        if (isSupportLink) {
          return (
            <a
              key={index}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="ai-link"
              style={{
                color: '#3953fb',
                fontWeight: 600,
                textDecoration: 'underline'
              }}
              title={`Open ${linkMatch[1]} in new tab`}
            >
              {linkMatch[1]}
              <span className="material-icons-outlined" style={{ fontSize: '12px', marginLeft: '3px', verticalAlign: 'middle' }}>open_in_new</span>
            </a>
          );
        }

        return (
          <span
            key={index}
            className="ai-link"
            onClick={() => handleRedirect(url)}
            style={{
              color: '#3953fb',
              fontWeight: 600,
              cursor: 'pointer',
              textDecoration: 'underline'
            }}
            title={`Go to ${linkMatch[1]}`}
          >
            {linkMatch[1]}
          </span>
        );
      }

      // If not a link, parse for Bold
      return <span key={index}>{parseBold(part)}</span>;
    });
  };

  const handleSend = async () => {
    if (!inputValue.trim()) return;

    const userMsg = inputValue.trim();
    setInputValue("");
    aiAgentStore.addMessage({ text: userMsg, sender: 'You', type: 'sent' });
    setLoading(true);

    try {
      const response = await GeminiService.chat(userMsg);
      aiAgentStore.addMessage({ text: response, sender: 'AI', type: 'received' });
    } catch (error) {
      console.error("AI Error:", error);
      aiAgentStore.addMessage({ text: `Error: ${error.message}`, sender: 'System', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") handleSend();
  };

  const renderMessageClass = (sender) => {
    if (sender === 'You') return 'from-user';
    if (sender === 'App') return 'from-app';
    return 'from-ai';
  };

  return (
    <div className={`aiAgentPanel ${isOpen ? "open" : ""} ${theme === "dark" ? "dark-mode" : ""}`}>
      <div className="agentHeader">
        <h3>
          AI Assistant
        </h3>
        <div className="header-controls">
          <span
            className="material-icons-outlined themeToggle"
            onClick={toggleTheme}
            title={`Switch to ${theme === 'light' ? 'Dark' : 'Light'} Mode`}
          >
            {theme === 'light' ? 'brightness_2' : 'wb_sunny'}
          </span>
          <span
            className="material-icons-outlined closeBtn"
            onClick={toggle}
            title="Close Panel"
          >
            close
          </span>
        </div>
      </div>

      <div className="agentContent">
        {aiAgentStore.messages.length === 0 && (
          <div className="welcomeMessage">
            <h4>How can I help you today?</h4>
            <p>I can help you navigate Salesgear, configure settings, or understand features.</p>
            <div className="suggestions">
              <div className="chip" onClick={() => setInputValue("How do I create a sequence?")}>Create Sequence</div>
              <div className="chip" onClick={() => setInputValue("Open Settings")}>Open Settings</div>
              <div className="chip" onClick={() => setInputValue("Import Contacts")}>Import Contacts</div>
            </div>
          </div>
        )}

        <div className="messagesList">
          {aiAgentStore.messages.map((msg, index) => (
            <div key={index} className={`message ${renderMessageClass(msg.sender)}`}>
              {msg.sender !== 'You' && <strong>{msg.sender === 'AI' ? 'Salesgear AI' : msg.sender}<br /></strong>}
              <div style={{ whiteSpace: 'pre-wrap' }}>
                {parseMessage(msg.text)}
              </div>
            </div>
          ))}
          {loading && (
            <div className="message from-ai loading">
              <span className="loading-dots">
                Thinking<span className="dot">.</span><span className="dot">.</span><span className="dot">.</span>
              </span>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      <div className="agentInputArea">
        <div className="inputWrapper">
          <input
            type="text"
            placeholder="Ask a question or type a command..."
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={loading}
            autoFocus
            maxLength={2000}
          />
          <button
            className="sendBtn"
            onClick={handleSend}
            disabled={!inputValue.trim() || loading}
          >
            <span className="material-icons-round">arrow_upward</span>
          </button>
        </div>
        <div style={{ textAlign: 'right', fontSize: '10px', color: '#999', marginTop: '4px', paddingRight: '10px' }}>
          {inputValue.length}/2000
        </div>
      </div>
    </div>
  );
});

export default AIAgentPanel;
